import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const SCRIPT_ID = "vnct-valueconnect-webchat-loader";

const DEPLOYMENT_KEY =
  process.env.REACT_APP_SF_CHAT_DEPLOYMENT_KEY || "DPK-DEMO";
const ORG_URL =
  process.env.REACT_APP_SF_CHAT_ORG_URL ||
  "https://codeyoung--uat.sandbox.my.salesforce-sites.com";

/**
 * Salesforce (ValueConnect) live-chat page, designed to be loaded inside the
 * StudentApp WebView (also works in a plain browser for testing):
 *
 *   #/salesforce-chat?parentId=<parentId>
 *
 * The vendor SDK requires `window.ValueConnectChatConfig` to be defined BEFORE
 * its loader script executes, so `parentId` travels in the URL (synchronous)
 * rather than via postMessage (racy). deploymentKey/orgUrl are env-driven so
 * vendor config changes need no app release.
 */
function SalesforceChat() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const parentId = params.get("parentId");
  const jwt = params.get("jwt");
  const [error, setError] = useState(
    parentId ? null : "Unable to start chat: missing account information.",
  );

  useEffect(() => {
    if (!parentId) return undefined;

    window.ValueConnectChatConfig = {
      deploymentKey: DEPLOYMENT_KEY,
      orgUrl: ORG_URL,
      autoOpen: true,
      // The widget is the whole page inside the app's WebView, so its own
      // close button would leave the parent staring at a blank screen with no
      // way back. The WebView's native header owns dismissal.
      hideCloseButton: true,
      accountId: parentId,
      ...(jwt ? { user: { jwt } } : {}),
    };

    let script = document.getElementById(SCRIPT_ID);
    if (!script) {
      script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.async = true;
      script.src = `${ORG_URL}/resource/ValueConnectWebChat/loader.js`;
      script.onerror = () =>
        setError("Live chat is unavailable right now. Please try again later.");
      document.body.appendChild(script);
    }

    return () => {
      // Cleanup so re-entering the page re-initializes the widget cleanly.
      const loader = document.getElementById(SCRIPT_ID);
      if (loader) loader.remove();
      delete window.ValueConnectChatConfig;
    };
  }, [parentId, jwt]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#ffffff",
        padding: 16,
        textAlign: "center",
        fontFamily: "inherit",
      }}
    >
      {error ? (
        <p style={{ color: "#333", fontSize: 16 }}>{error}</p>
      ) : (
        <p style={{ color: "#999", fontSize: 14 }}>Starting live chat…</p>
      )}
    </div>
  );
}

export default SalesforceChat;
